import { useCallback, useEffect, useState } from 'react'
import { Button, Input, buttonVariants } from '@heroui/react'
import { api } from '../lib/api'
import { listState } from '../lib/listState'
import { alertsToSend, rowProblem, rowsFrom, type ThresholdRow } from '../lib/notifications'
import { formatDuration, nameIn } from '../lib/staffText'
import type { Queue } from '../lib/types'
import { Icon } from '../ui/icons'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { useLiveBranch } from './liveBranch'
import { Loading } from './Loading'
import { Messages, useAction } from './parts'
import { Toggle } from './sectionKit'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, PageHead, RowArrow, useOpenRecord } from './settingsKit'
import { EmptyState } from '../ui/parts'
import '../styles/orgset.css'

/**
 * Queue alerts: when each queue on this branch's floor raises an alert, when it
 * lowers it, and who is texted. Two levels per queue — how many are waiting, how
 * long the longest has waited — either of which raises the alert; it clears only
 * when both are back at or below their clear level, so it does not flap.
 *
 * Saved at once and in force at once — not part of the draft, because a threshold
 * decides nobody's place in a line (docs/BRANCH-CONFIG.md, "What is in the
 * draft"). The whole set is saved together, as the route takes it.
 *
 * Each row shows the queue as the floor has it right now, from the live channel,
 * and says so when its alert is up.
 */

const MAX_NUMBERS = 3

type AlertQueue = { id: string; code: string; name: Record<string, string> }
type Chip = 'all' | 'set' | 'raised'

const numbersOf = (typed: string) =>
  typed
    .split(',')
    .map((n) => n.trim())
    .filter(Boolean)

export function QueueAlerts({ branchId }: { branchId: string }) {
  const { lang, say } = useStaffText()
  const shapesQueues = useSession()?.identity.permissions.includes('profiles.manage') ?? false
  const [rows, setRows] = useState<ThresholdRow[]>([])
  const [queues, setQueues] = useState<AlertQueue[]>([])
  const [routing, setRouting] = useState<Queue[]>([])
  const [loaded, setLoaded] = useState(false)
  // A queue's alert opens on its own page (#alerts/<branch code>/<queue id>).
  const rec = useOpenRecord(rows, (r) => r.queueId, loaded)
  const editing = rec.row
  const [find, setFind] = useState('')
  const [chip, setChip] = useState<Chip>('all')
  const live = useLiveBranch(branchId)

  const load = useCallback(async () => {
    const r = await api.queueAlerts(branchId)
    setQueues(r.queues)
    setRows(rowsFrom(r.alerts, r.queues))
    setLoaded(true)
  }, [branchId])
  const { busy, error, notice, run, setError } = useAction(load)

  useEffect(() => {
    load().catch(() => {
      setError(say('notify.alerts.notLoaded'))
      setLoaded(true)
    })
  }, [load, setError, say])

  // Only for the ticket letters on each row.
  useEffect(() => {
    api
      .queues(branchId)
      .then((q) => setRouting(q.queues ?? []))
      .catch(() => undefined)
  }, [branchId])

  const letter = (q: AlertQueue) => {
    const routed = routing.find((x) => x.id === q.id)
    return routed?.letter || q.code.slice(0, 1)
  }
  const on = rows.filter((r) => r.waitingAlert > 0 || r.longestAlertMin > 0).length
  const levels = (r: ThresholdRow) => {
    const parts: string[] = []
    if (r.waitingAlert > 0) parts.push(say('catalogue.alerts.waitingLevel', { n: r.waitingAlert }))
    if (r.longestAlertMin > 0) parts.push(say('catalogue.alerts.longestLevel', { n: r.longestAlertMin }))
    return parts.join(say('calling.listOr'))
  }

  const q = find.trim().toLocaleLowerCase()
  const isSet = (r: ThresholdRow) => r.waitingAlert > 0 || r.longestAlertMin > 0
  const isRaised = (r: ThresholdRow) => Boolean(live?.alerts.some((a) => a.queueId === r.queueId))
  const raisedCount = rows.filter(isRaised).length
  // A chip whose rows have gone falls back to All.
  const view: Chip = on === 0 || (chip === 'raised' && raisedCount === 0) ? 'all' : chip
  const shown = rows.filter((r) => {
    const queue = queues.find((x) => x.id === r.queueId)
    if (!queue) return false
    if (view === 'set' && !isSet(r)) return false
    if (view === 'raised' && !isRaised(r)) return false
    return !q || nameIn(queue.name, lang, queue.code).toLocaleLowerCase().includes(q) || queue.code.toLocaleLowerCase().includes(q)
  })

  const editingQueue = editing && queues.find((x) => x.id === editing.queueId)
  if (editing && editingQueue) {
    return (
      <>
        <Messages error="" notice={notice} />
        <AlertSheet
          key={editing.queueId}
          row={editing}
          queue={editingQueue}
          letter={letter(editingQueue)}
          busy={busy}
          error={error}
          onClose={() => {
            rec.close()
            setError('')
          }}
          onSave={(next) => {
            const toSend = alertsToSend(rows.map((r) => (r.queueId === next.queueId ? next : r)))
            if (!toSend) {
              const bad = rowProblem(next)
              if (bad) setError(say(bad))
              return
            }
            void run(async () => {
              await api.saveQueueAlerts(branchId, toSend)
              rec.close()
            }, say('catalogue.alerts.saved'))
          }}
        />
      </>
    )
  }

  return (
    <>
      <PageHead title={say('admin.section.alerts')} line={say('settings.alerts.line')} />
      <Messages error={editing ? '' : error} notice={notice} />

      <ListCard<Chip>
        title={say('settings.alerts.list')}
        count={loaded ? say('catalogue.alerts.count', { n: on, total: queues.length }) : undefined}
        search={find}
        onSearch={rows.length > 6 ? setFind : undefined}
        searchLabel={say('settings.alerts.find')}
        chips={
          on > 0
            ? [
                { value: 'all', label: say('settings.alerts.all') },
                { value: 'set', label: say('settings.alerts.set'), count: on },
                ...(raisedCount > 0 ? [{ value: 'raised' as const, label: say('settings.alerts.raised'), count: raisedCount }] : []),
              ]
            : undefined
        }
        chip={view}
        onChip={setChip}
      >
        {listState(loaded, rows.length) === 'loading' && <Loading />}
        {listState(loaded, rows.length) === 'empty' && (
          <EmptyState
            title={say('catalogue.queues.noLines')}
            action={
              shapesQueues && (
                <a className={buttonVariants({ variant: 'secondary' })} href="#opProfiles">
                  {say('catalogue.num.openQueues')}
                </a>
              )
            }
          />
        )}
        {rows.length > 0 && shown.length === 0 && <EmptyState title={say('admin.findNone', { q: find.trim() })} />}

        {shown.length > 0 && (
          <ListTable
            label={say('settings.alerts.list')}
            cols="minmax(220px, 1.4fr) minmax(150px, 0.9fr) minmax(180px, 1fr) 16px"
            head={[say('record.col.queue'), say('record.col.texts'), say('record.col.now'), '']}
          >
            {shown.map((r) => {
              const queue = queues.find((x) => x.id === r.queueId)!
              const set = isSet(r)
              const now = live?.queues.find((x) => x.queueId === r.queueId)
              const raised = isRaised(r)
              const texts = numbersOf(r.textTo)
              return (
                <ListRow
                  key={r.queueId}
                  onOpen={() => {
                    setError('')
                    rec.open(r)
                  }}
                >
                  <NameCell
                    tile={<IconTile text={letter(queue)} tone={raised ? 4 : set ? 2 : 1} />}
                    name={nameIn(queue.name, lang, queue.code)}
                    sub={set ? levels(r) : say('catalogue.alerts.off')}
                  />
                  <span className="os-alert__who">
                    {set &&
                      (texts.length > 0 ? (
                        <>
                          <PhoneMark />
                          {texts.length === 1 ? texts[0] : say('catalogue.alerts.nNumbers', { n: texts.length })}
                        </>
                      ) : (
                        <span className="set-muted">{say('catalogue.alerts.noTexts')}</span>
                      ))}
                  </span>
                  <span className="os-alert__now" data-raised={raised || undefined}>
                    {now && (
                      <>
                        {raised && (
                          <span className="os-alert__nowlabel">
                            <BellMark /> {say('catalogue.alerts.raised')}
                          </span>
                        )}
                        <span>
                          {now.waiting > 0
                            ? say('catalogue.alerts.nowLine', { n: now.waiting, wait: formatDuration(now.longestWaitSec, lang) })
                            : say('catalogue.alerts.nobody')}
                        </span>
                      </>
                    )}
                  </span>
                  <RowArrow />
                </ListRow>
              )
            })}
          </ListTable>
        )}
      </ListCard>

    </>
  )
}

function AlertSheet({
  row,
  queue,
  letter,
  busy,
  error,
  onClose,
  onSave,
}: {
  row: ThresholdRow
  queue: AlertQueue
  letter: string
  busy: boolean
  error: string
  onClose: () => void
  onSave: (row: ThresholdRow) => void
}) {
  const { lang, say } = useStaffText()
  const [d, setD] = useState<ThresholdRow>(row)
  const [phones, setPhones] = useState<string[]>(() => {
    const listed = numbersOf(row.textTo)
    return listed.length ? listed : ['']
  })
  const next: ThresholdRow = { ...d, textTo: phones.map((n) => n.trim()).filter(Boolean).join(', ') }
  const problem = rowProblem(next)
  const any = next.waitingAlert > 0 || next.longestAlertMin > 0
  const changed = JSON.stringify(next) !== JSON.stringify(row)

  return (
    <InnerPage
      back={say('admin.section.alerts')}
      title={nameIn(queue.name, lang, queue.code)}
      mark={<span className="stub stub--lg">{letter}</span>}
      onClose={onClose}
      onSubmit={() => onSave(next)}
      submitLabel={say('catalogue.alerts.save')}
      valid={!problem && changed}
      busy={busy}
      error={error}
    >
      <Level
        title={say('catalogue.alerts.tooMany')}
        raiseUnit={say('catalogue.alerts.people')}
        on={d.waitingAlert > 0}
        alert={d.waitingAlert}
        reset={d.waitingReset}
        start={[10, 6]}
        onChange={(waitingAlert, waitingReset) => setD({ ...d, waitingAlert, waitingReset })}
      />
      <Level
        title={say('catalogue.alerts.tooLong')}
        raiseUnit={say('catalogue.alerts.minutes')}
        on={d.longestAlertMin > 0}
        alert={d.longestAlertMin}
        reset={d.longestResetMin}
        start={[20, 10]}
        onChange={(longestAlertMin, longestResetMin) => setD({ ...d, longestAlertMin, longestResetMin })}
      />

      <fieldset className="field" disabled={!any}>
        <legend title={`${say('catalogue.alerts.textHelp')} ${say('catalogue.alerts.managerMail')}`}>{say('catalogue.alerts.textThese')}</legend>
        {!any && <p className="os-help">{say('catalogue.alerts.levelFirst')}</p>}
        <ul className="os-phones">
          {phones.map((n, i) => (
            <li key={i}>
              <Input
                fullWidth
                inputMode="tel"
                value={n}
                placeholder="+994 50 123 45 67"
                aria-label={say('catalogue.alerts.phoneN', { n: i + 1 })}
                onChange={(e) => setPhones(phones.map((x, j) => (j === i ? e.target.value : x)))}
              />
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                aria-label={say('common.remove')}
                onPress={() => setPhones(phones.length === 1 ? [''] : phones.filter((_, j) => j !== i))}
              >
                <Icon name="close" size={18} />
              </Button>
            </li>
          ))}
        </ul>
        {phones.length < MAX_NUMBERS && any && (
          <Button variant="ghost" size="sm" className="admin-start" onPress={() => setPhones([...phones, ''])}>
            <Icon name="plus" size={14} /> {say('catalogue.alerts.addNumber')}
          </Button>
        )}
      </fieldset>

      {problem && <p className="os-problem" role="alert">{say(problem)}</p>}
    </InnerPage>
  )
}

function Level({
  title,
  raiseUnit,
  on,
  alert,
  reset,
  start,
  onChange,
}: {
  title: string
  raiseUnit: string
  on: boolean
  alert: number
  reset: number
  /** What switching the level on starts from: raise, clear. */
  start: [number, number]
  onChange: (alert: number, reset: number) => void
}) {
  const { say } = useStaffText()
  return (
    <div className="os-level">
      <Toggle title={title} checked={on} onChange={(v) => (v ? onChange(start[0], start[1]) : onChange(0, 0))} />
      {on && (
        <div className="os-level__pair">
          <label className="field">
            <span>{say('catalogue.alerts.raiseAt')}</span>
            <span className="os-unit">
              <Input
                type="number"
                min={1}
                value={alert}
                onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0), reset)}
              />
              <span>{raiseUnit}</span>
            </span>
          </label>
          <label className="field">
            <span>{say('catalogue.alerts.clearAt')}</span>
            <span className="os-unit">
              <Input
                type="number"
                min={0}
                value={reset}
                onChange={(e) => onChange(alert, Math.max(0, Number(e.target.value) || 0))}
              />
              <span>{raiseUnit}</span>
            </span>
          </label>
        </div>
      )}
    </div>
  )
}

function PhoneMark() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 3h10v18H7zM11 18h2" />
    </svg>
  )
}

function BellMark() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20.5a2.2 2.2 0 0 0 4 0" />
    </svg>
  )
}
